import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { app, storage } from '../firebase';
import { useAuth } from '../AuthContext';
import { useEditMode } from '../AuthContext';

const Gallery: React.FC = () => {
  const { user } = useAuth();
  const { editMode } = useEditMode();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState<any>(null);
  const [gallery, setGallery] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editPlaceholder, setEditPlaceholder] = useState<File | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);

  // Upload form state
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState('');
  const [type, setType] = useState<'image' | 'video'>('image');
  const [placeholder, setPlaceholder] = useState<File | null>(null);
  const [title, setTitle] = useState(''); // NEW: Title state

  // NEW: For editing title
  const [editTitleId, setEditTitleId] = useState<string | null>(null);
  const [editTitleValue, setEditTitleValue] = useState('');

  useEffect(() => {
    const fetchGallery = async () => {
      setLoading(true);
      const db = getFirestore(app);
      const q = query(collection(db, 'gallery'), orderBy('created', 'desc'));
      const querySnapshot = await getDocs(q);
      setGallery(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    };
    fetchGallery();
  }, []);

  const openModal = (item: any) => {
    setModalItem(item);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalItem(null);
  };

  // Upload handler
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploadError('');
    if (!file || !alt) {
      setUploadError('File and alt text are required.');
      return;
    }
    // Placeholder is optional for videos
    setUploading(true);
    try {
      // Upload main file
      const mainRef = ref(storage, `gallery/${Date.now()}-${file.name}`);
      await uploadBytes(mainRef, file);
      const fileUrl = await getDownloadURL(mainRef);
      let placeholderUrl = '';
      if (type === 'video' && placeholder) {
        const phRef = ref(storage, `gallery/placeholders/${Date.now()}-${placeholder.name}`);
        await uploadBytes(phRef, placeholder);
        placeholderUrl = await getDownloadURL(phRef);
      }
      // Save metadata to Firestore
      const db = getFirestore(app);
      await addDoc(collection(db, 'gallery'), {
        src: fileUrl,
        alt,
        type,
        placeholder: placeholderUrl,
        created: Date.now(),
        title: title.trim(),
      });
      setFile(null); setAlt(''); setType('image'); setPlaceholder(null); setTitle('');
      setShowUploadForm(false); // Hide form after successful upload
      // Refresh gallery
      const q = query(collection(db, 'gallery'), orderBy('created', 'desc'));
      const querySnapshot = await getDocs(q);
      setGallery(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err: any) {
      setUploadError('Upload failed: ' + (err?.message || String(err)));
    }
    setUploading(false);
  };

  // Edit placeholder for video
  const handleEditPlaceholder = async (id: string) => {
    if (!editPlaceholder) return;
    setUploading(true);
    try {
      const phRef = ref(storage, `gallery/placeholders/${Date.now()}-${editPlaceholder.name}`);
      await uploadBytes(phRef, editPlaceholder);
      const placeholderUrl = await getDownloadURL(phRef);
      const db = getFirestore(app);
      await updateDoc(doc(db, 'gallery', id), { placeholder: placeholderUrl });
      // Refresh gallery
      const q = query(collection(db, 'gallery'), orderBy('created', 'desc'));
      const querySnapshot = await getDocs(q);
      setGallery(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setEditId(null); setEditPlaceholder(null);
    } catch (err) {
      // handle error
    }
    setUploading(false);
  };

  // Delete gallery item
  const handleDelete = async (id: string, item: any) => {
    setUploading(true);
    try {
      const db = getFirestore(app);
      
      // Delete from Firestore
      await deleteDoc(doc(db, 'gallery', id));
      
      // Delete main file from Storage
      const mainRef = ref(storage, item.src.split('/o/')[1]?.split('?')[0]);
      if (mainRef) {
        try {
          await deleteObject(mainRef);
        } catch (err) {
          console.log('Main file not found in storage, continuing...');
        }
      }
      
      // Delete placeholder file from Storage if it exists
      if (item.placeholder) {
        const placeholderRef = ref(storage, item.placeholder.split('/o/')[1]?.split('?')[0]);
        if (placeholderRef) {
          try {
            await deleteObject(placeholderRef);
          } catch (err) {
            console.log('Placeholder file not found in storage, continuing...');
          }
        }
      }
      
      // Refresh gallery
      const q = query(collection(db, 'gallery'), orderBy('created', 'desc'));
      const querySnapshot = await getDocs(q);
      setGallery(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setDeleteId(null);
    } catch (err) {
      console.error('Error deleting item:', err);
    }
    setUploading(false);
  };

  // Add this function to handle title update
  const handleEditTitle = async (id: string) => {
    if (!editTitleValue.trim()) return;
    setUploading(true);
    try {
      const db = getFirestore(app);
      await updateDoc(doc(db, 'gallery', id), { title: editTitleValue.trim() });
      // Refresh gallery
      const q = query(collection(db, 'gallery'), orderBy('created', 'desc'));
      const querySnapshot = await getDocs(q);
      setGallery(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setEditTitleId(null);
      setEditTitleValue('');
    } catch (err) {
      // handle error
    }
    setUploading(false);
  };

  return (
    <div className="page-container">
      <div className="max-w-7xl mx-auto px-4 pb-12">
      <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">
        <span className="gradient-text">Memory Lane</span>
      </h1>
      <p className="text-center text-text-secondary mb-8 max-w-2xl mx-auto">
        Log in to upload your favorite raid, M+, or guild moments to the gallery!
      </p>
      {/* Upload Form for Any Logged-in User */}
      {user && (
        <div className="mb-8 max-w-lg mx-auto">
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="w-full btn-neon py-3 rounded-xl font-bold mb-4 flex items-center justify-center gap-2 hover:bg-cyan-700 transition-colors"
          >
            <span>{showUploadForm ? 'Hide Upload Form' : 'Upload to Gallery'}</span>
            <svg 
              className={`w-5 h-5 transition-transform ${showUploadForm ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showUploadForm && (
            <form onSubmit={handleUpload} className="glass p-6 rounded-xl space-y-4">
              {uploadError && <div className="text-red-400 text-center mb-2">{uploadError}</div>}
              <div>
                <label className="block mb-1 font-medium">File</label>
                <input type="file" accept="image/*,video/*" onChange={e => {
                  const f = e.target.files?.[0] || null;
                  setFile(f);
                  if (f && f.type.startsWith('video')) setType('video');
                  else setType('image');
                }} required />
              </div>
              <div>
                <label className="block mb-1 font-medium">Title</label> {/* NEW: Title input */}
                <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400" placeholder="Enter a title (optional)" />
              </div>
              <div>
                <label className="block mb-1 font-medium">Alt Text</label>
                <input type="text" value={alt} onChange={e => setAlt(e.target.value)} className="w-full p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400" required />
              </div>
              {type === 'video' && (
                <div>
                  <label className="block mb-1 font-medium">Placeholder Image (optional - for video grid display)</label>
                  <input type="file" accept="image/*" onChange={e => setPlaceholder(e.target.files?.[0] || null)} />
                  <p className="text-sm text-gray-400 mt-1">If no placeholder is provided, a default play button will be shown</p>
                </div>
              )}
              <button type="submit" className="w-full btn-neon py-2 rounded font-bold mt-2" disabled={uploading}>{uploading ? 'Uploading...' : 'Upload'}</button>
            </form>
          )}
        </div>
      )}
      {/* Gallery Grid */}
      {loading ? (
        <div className="text-center text-text-secondary">Loading gallery...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {gallery.map((item) => (
            <div
              key={item.id}
              className="bg-gray-800 rounded-lg flex flex-col items-center justify-center overflow-hidden cursor-pointer hover:ring-4 hover:ring-cyan-400 transition-all relative"
              onClick={() => openModal(item)}
            >
              {item.type === 'image' ? (
                <>
                  <img src={item.src} alt={item.alt} className="object-cover w-full h-64" />
                  {item.title && (
                    <div className="w-full px-2 py-2 bg-black/70 text-center text-cyan-200 font-semibold text-lg truncate">{item.title}</div>
                  )}
                  {user && user.role === 'admin' && editMode && (
                    <div className="absolute top-2 right-2 flex gap-1 z-10">
                      <button
                        className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                        onClick={e => { e.stopPropagation(); setDeleteId(item.id); }}
                      >
                        Delete
                      </button>
                      <button
                        className="bg-cyan-600 text-white px-2 py-1 rounded text-xs hover:bg-cyan-700"
                        onClick={e => {
                          e.stopPropagation();
                          setEditTitleId(item.id);
                          setEditTitleValue(item.title || '');
                        }}
                      >
                        Edit Title
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {item.placeholder ? (
                    <img src={item.placeholder} alt={item.alt + ' placeholder'} className="object-fill w-full h-64" />
                  ) : (
                    <div className="w-full h-64 bg-gray-700 flex items-center justify-center">
                      <div className="text-center">
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg mx-auto mb-2">
                          <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.5)" />
                          <polygon points="26,20 48,32 26,44" fill="#00ffff" />
                        </svg>
                        <p className="text-gray-400 text-sm">Video</p>
                      </div>
                    </div>
                  )}
                  {item.title && (
                    <div className="w-full px-2 py-2 bg-black/70 text-center text-cyan-200 font-semibold text-lg truncate">{item.title}</div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
                      <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.5)" />
                      <polygon points="26,20 48,32 26,44" fill="#00ffff" />
                    </svg>
                  </div>
                  {user && user.role === 'admin' && editMode && (
                    <div className="absolute top-2 right-2 flex gap-1 z-10">
                      {item.type === 'video' && (
                        <button
                          className="bg-cyan-600 text-white px-2 py-1 rounded text-xs hover:bg-cyan-700"
                          onClick={e => { e.stopPropagation(); setEditId(item.id); }}
                        >
                          Edit Placeholder
                        </button>
                      )}
                      <button
                        className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                        onClick={e => { e.stopPropagation(); setDeleteId(item.id); }}
                      >
                        Delete
                      </button>
                      <button
                        className="bg-cyan-600 text-white px-2 py-1 rounded text-xs hover:bg-cyan-700"
                        onClick={e => {
                          e.stopPropagation();
                          setEditTitleId(item.id);
                          setEditTitleValue(item.title || '');
                        }}
                      >
                        Edit Title
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Edit Placeholder Modal */}
      {editId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80" onClick={() => setEditId(null)}>
          <div className="relative max-w-md w-full mx-4 bg-surface p-8 rounded-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4 text-center">Edit Placeholder Image</h2>
            <input type="file" accept="image/*" onChange={e => setEditPlaceholder(e.target.files?.[0] || null)} />
            <div className="flex justify-center gap-4 mt-6">
              <button
                className="btn-neon px-6 py-2 rounded font-bold"
                onClick={() => handleEditPlaceholder(editId)}
                disabled={uploading || !editPlaceholder}
              >
                {uploading ? 'Saving...' : 'Save'}
              </button>
              <button
                className="btn px-6 py-2 rounded font-bold"
                onClick={() => setEditId(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80" onClick={() => setDeleteId(null)}>
          <div className="relative max-w-md w-full mx-4 bg-surface p-8 rounded-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4 text-center text-red-400">Delete Gallery Item</h2>
            <p className="text-center mb-6 text-gray-300">
              Are you sure you want to delete this item? This action cannot be undone.
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded font-bold"
                onClick={() => {
                  const item = gallery.find(g => g.id === deleteId);
                  if (item) handleDelete(deleteId, item);
                }}
                disabled={uploading}
              >
                {uploading ? 'Deleting...' : 'Delete'}
              </button>
              <button
                className="btn px-6 py-2 rounded font-bold"
                onClick={() => setDeleteId(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Title Modal */}
      {editTitleId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80" onClick={() => setEditTitleId(null)}>
          <div className="relative max-w-md w-full mx-4 bg-surface p-8 rounded-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4 text-center">Edit Title</h2>
            <input
              type="text"
              className="w-full p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400 mb-4"
              value={editTitleValue}
              onChange={e => setEditTitleValue(e.target.value)}
              placeholder="Enter a new title"
              autoFocus
            />
            <div className="flex justify-center gap-4 mt-6">
              <button
                className="btn-neon px-6 py-2 rounded font-bold"
                onClick={() => handleEditTitle(editTitleId)}
                disabled={uploading || !editTitleValue.trim()}
              >
                {uploading ? 'Saving...' : 'Save'}
              </button>
              <button
                className="btn px-6 py-2 rounded font-bold"
                onClick={() => setEditTitleId(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal for full-size image or video */}
      {modalOpen && modalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80" onClick={closeModal}>
          <div className="relative max-w-3xl w-full mx-4" onClick={e => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-white text-3xl font-bold hover:text-cyan-400 z-10"
              onClick={closeModal}
              aria-label="Close"
            >
              &times;
            </button>
            {modalItem.type === 'image' ? (
              <img src={modalItem.src} alt="Full size" className="w-full h-auto rounded-lg shadow-lg" />
            ) : (
              <video 
                src={modalItem.src} 
                controls 
                autoPlay 
                className="w-full h-auto rounded-lg shadow-lg bg-black" 
                title={modalItem.alt} 
                poster={modalItem.placeholder || undefined} 
              />
            )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default Gallery;